import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Form, Tabs, Modal, Button, message } from 'antd';

// 導入分拆的 Hooks
import { useWorkflowState } from './hooks/useWorkflowState';
import { useDataFetching } from './hooks/useDataFetching';
import { useNodeHandlers } from './hooks/useNodeHandlers';
import { useEdgeHandlers } from './hooks/useEdgeHandlers';
import { useWorkflowSave } from './hooks/useWorkflowSave';
import { useAdvancedFeatures } from './hooks/useAdvancedFeatures';
import { useNodeSelection } from './hooks/useNodeSelection';

// 導入分拆的組件
import Toolbar from './components/Toolbar';
import Sidebar from './components/Sidebar';
import WorkflowCanvas from './components/WorkflowCanvas';
import NodePropertyDrawer from './components/NodePropertyDrawer';
import WorkflowDesignerAIChat from './components/WorkflowDesignerAIChat';
import AIGeneratingOverlay from './components/AIGeneratingOverlay';

// 導入模態框
import TemplateModal from './modals/TemplateModal';
import UserModal from './modals/UserModal';
import EFormModal from './modals/EFormModal';
import ProcessVariablesModal from './modals/ProcessVariablesModal';
import ConditionModal from './modals/ConditionModal';
import ConditionGroupModal from './modals/ConditionGroupModal';
import DefaultPathModal from './modals/DefaultPathModal';

// 導入樣式
import { purpleButtonStyle } from './styles';
import './WorkflowDesigner.css';
import { getAvailableOutputPaths, hydrateFlowFromParsedJson } from './utils';

const WhatsAppWorkflowDesignerRefactored = ({ workflowId: workflowIdProp }) => {
  const { t, isReady } = useLanguage();
  const [processVariableForm] = Form.useForm();
  
  // 使用節點選中 Hook
  const {
    selectedNodes,
    setSelectedNodes,
    isNodeSelected,
    handleNodeSelect,
    handleCanvasClick,
    clearSelection,
    selectAllNodes,
    handleSelectionChange,
    syncSelectionToReactFlow
  } = useNodeSelection();

  // 使用工作流程狀態 Hook
  const {
    nodes,
    setNodes,
    onNodesChange,
    edges,
    setEdges,
    onEdgesChange,
    selectedNode,
    setSelectedNode,
    selectedEdge,
    setSelectedEdge,
    hoveredEdge,
    setHoveredEdge,
    drawerOpen,
    setDrawerOpen,
    isToolbarCollapsed,
    setIsToolbarCollapsed,
    isConnecting,
    setIsConnecting,
    connectionStart,
    setConnectionStart,
    reactFlowInstanceRef,
    form,
    nodeTypes,
    nodeTypesComponents,
    nodeTypeLabel,
    handleNodeDataChange,
    onNodeClick,
    onNodeDoubleClick,
    onEdgeClick,
    onEdgeMouseEnter,
    onEdgeMouseLeave,
    onMove,
    handleInit,
    toggleToolbar,
    handleEdgeSwitch,
    edgeTypes,
    setGlobalDragHandler,
  } = useWorkflowState(isNodeSelected);

  // 使用高級功能 Hook
  const {
    copyNodes,
    pasteNodes,
    alignNodes,
    handleKeyDown: originalHandleKeyDown,
  } = useAdvancedFeatures(nodes, setNodes, edges, setEdges, selectedNodes, setSelectedNodes, nodeTypes);

  // Time Validator 模板選擇相關狀態
  const [templateModalSource, setTemplateModalSource] = useState(null);
  
  // 監聽 QR Code 模板選擇請求
  useEffect(() => {
    const handleQRCodeTemplateSelectRequest = (event) => {
      const { messageType } = event.detail;
      console.log('🎯 WhatsAppWorkflowDesigner 收到 QR Code 模板選擇請求:', { messageType });
      window.qrCodeTemplateSelectMessageType = messageType;
    };

    window.addEventListener('qrCodeTemplateSelectRequest', handleQRCodeTemplateSelectRequest);
    
    return () => {
      window.removeEventListener('qrCodeTemplateSelectRequest', handleQRCodeTemplateSelectRequest);
      window.qrCodeTemplateSelectMessageType = null;
    };
  }, []);

  // 監聽 Wait Reply 模板選擇請求
  useEffect(() => {
    const handleWaitReplyTemplateSelectRequest = (event) => {
      const { messageType } = event.detail;
      console.log('🎯 WhatsAppWorkflowDesigner 收到 Wait Reply 模板選擇請求:', { messageType });
      window.waitReplyTemplateSelectMessageType = messageType;
    };

    window.addEventListener('waitReplyTemplateSelectRequest', handleWaitReplyTemplateSelectRequest);
    
    return () => {
      window.removeEventListener('waitReplyTemplateSelectRequest', handleWaitReplyTemplateSelectRequest);
      window.waitReplyTemplateSelectMessageType = null;
    };
  }, []);

  // 處理 Time Validator 模板選擇
  const handleTimeValidatorTemplateSelect = useCallback((template, isMetaTemplate) => {
    console.log('🎯 WhatsAppWorkflowDesigner 處理 Time Validator 模板選擇:', { template: template.name, isMetaTemplate, source: templateModalSource });
    
    // 這裡需要通知 NodePropertyDrawer 來處理模板選擇
    // 我們通過自定義事件來實現
    const event = new CustomEvent('timeValidatorTemplateSelected', {
      detail: { template, isMetaTemplate, source: templateModalSource }
    });
    window.dispatchEvent(event);
    
    // 重置 templateModalSource
    setTemplateModalSource(null);
  }, [templateModalSource]);

  // 包裝鍵盤事件處理
  const handleKeyDown = useCallback((event) => {
    // 檢查是否在輸入框、文本區域或可編輯元素中
    const target = event.target;
    const isInputElement = target.tagName === 'INPUT' || 
                          target.tagName === 'TEXTAREA' || 
                          target.contentEditable === 'true' ||
                          target.isContentEditable ||
                          // 檢查是否在 Ant Design 的輸入組件中
                          target.closest('.ant-input') ||
                          target.closest('.ant-input-affix-wrapper') ||
                          target.closest('.ant-select-selector') ||
                          target.closest('.ant-cascader-picker') ||
                          target.closest('.ant-picker') ||
                          target.closest('.ant-mentions') ||
                          target.closest('.ant-upload');
    
    // 如果在輸入元素中，不攔截鍵盤事件
    if (isInputElement) {
      return; // 讓瀏覽器處理輸入事件
    }
    
    originalHandleKeyDown(event);
    
    // 處理全選功能
    if (event.ctrlKey || event.metaKey) {
      if (event.key === 'a') {
        event.preventDefault();
        selectAllNodes(nodes);
        // 同步選擇狀態到 React Flow
        if (reactFlowInstanceRef.current) {
          syncSelectionToReactFlow(reactFlowInstanceRef.current, selectedNodes);
        }
      }
    }
  }, [originalHandleKeyDown, selectAllNodes, nodes, syncSelectionToReactFlow, selectedNodes, reactFlowInstanceRef]);

  // 使用數據獲取 Hook
  const {
    name,
    setName,
    description,
    setDescription,
    status,
    setStatus,
    workflowId,
    templates,
    setTemplates,
    metaTemplates,
    setMetaTemplates,
    isTemplateModalVisible,
    setIsTemplateModalVisible,
    selectedTemplate,
    setSelectedTemplate,
    users,
    setUsers,
    isUserModalVisible,
    setIsUserModalVisible,
    selectedUser,
    setSelectedUser,
    eforms,
    setEforms,
    isEFormModalVisible,
    setIsEFormModalVisible,
    selectedEForm,
    setSelectedEForm,
    isRecipientModalVisible,
    setIsRecipientModalVisible,
    isWorkflowLoading,
    nodeTypeDefinitions,
    setNodeTypeDefinitions,
    loadingNodeTypes,
    setLoadingNodeTypes,
    processVariables,
    setProcessVariables,
    processVariablesModalVisible,
    setProcessVariablesModalVisible,
    selectedProcessVariable,
    setSelectedProcessVariable,
    editingProcessVariable,
    setEditingProcessVariable,
    initializeData,
    lastLoadedFlowJson,
    setLastLoadedFlowJson,
  } = useDataFetching(workflowIdProp != null ? String(workflowIdProp) : undefined);

  // 使用節點處理 Hook
  const {
    handleDeleteNode,
    handleAddNode,
    onDragStart,
    onDragOver,
    onDrop,
        handleSelectTemplate,
        handleSelectUser,
        handleSelectEForm,
  } = useNodeHandlers(nodeTypes, setNodes, setEdges, setSelectedNode, selectedNode, handleNodeDataChange, isReady, t);

  // 使用邊處理 Hook
  const {
    onConnect,
    handleDeleteEdge,
    onEdgeDelete,
    onEdgeMouseEnter: onEdgeMouseEnterHandler,
    onEdgeMouseLeave: onEdgeMouseLeaveHandler,
    onMove: onMoveHandler,
    onEdgeSwitch: onEdgeSwitchHandler,
  } = useEdgeHandlers(nodes, edges, setEdges, setSelectedEdge, setHoveredEdge, handleEdgeSwitch);

  const flowJson = useMemo(() => {
    const cleanNodes = nodes.map((node) => ({
      ...node,
      data: { ...node.data, icon: undefined }
    }));
    const cleanEdges = edges.map((edge) => ({
      ...edge,
      data: { ...(edge.data || {}), onEdgeSwitch: undefined }
    }));
    return JSON.stringify({ nodes: cleanNodes, edges: cleanEdges });
  }, [nodes, edges]);

  // 使用工作流程保存 Hook（保存成功後更新 lastLoadedFlowJson，以便 isDirty 正確）
  const { handleSave } = useWorkflowSave(
    nodes,
    edges,
    name,
    description,
    status,
    null, // createdAt
    null, // createdBy
    null, // updatedBy
    workflowId,
    t,
    (savedFlowJson) => setLastLoadedFlowJson(savedFlowJson || flowJson)
  );

  const isDirty = flowJson !== lastLoadedFlowJson;

  const importFlowFileInputRef = useRef(null);

  const handleExportFlowJson = useCallback(() => {
    try {
      const parsed = JSON.parse(flowJson);
      const payload = {
        workflowExportVersion: 1,
        exportedAt: new Date().toISOString(),
        name: name || '',
        description: description || '',
        nodes: parsed.nodes,
        edges: parsed.edges,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const base = (name || 'workflow').replace(/[<>:"/\\|?*]/g, '_');
      a.download = `${base}.json`;
      a.click();
      URL.revokeObjectURL(url);
      message.success(t('workflowDesigner.exportFlowJsonSuccess'));
    } catch (err) {
      console.error(err);
      message.error(t('workflowDesigner.exportFlowJsonFailed'));
    }
  }, [flowJson, name, description, t]);

  const handleImportFlowJsonClick = useCallback(() => {
    const openFile = () => importFlowFileInputRef.current?.click();
    if (isDirty) {
      Modal.confirm({
        title: t('workflowDesigner.importFlowJsonConfirmTitle'),
        content: t('workflowDesigner.importFlowJsonConfirmContent'),
        okText: t('workflowDesigner.importFlowJsonConfirmOk'),
        cancelText: t('workflowDesigner.importFlowJsonConfirmCancel'),
        onOk: openFile,
      });
    } else {
      openFile();
    }
  }, [isDirty, t]);

  const handleImportFlowJsonFileChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.name.toLowerCase().endsWith('.json')) {
        message.error(t('workflowDesigner.importFlowJsonInvalidFile'));
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const data = JSON.parse(reader.result);
          const flow = data.nodes != null ? data : null;
          if (!flow || !Array.isArray(flow.nodes)) {
            message.error(t('workflowDesigner.importFlowJsonFailed'));
            e.target.value = '';
            return;
          }
          const { nodes: nextNodes, edges: nextEdges } = hydrateFlowFromParsedJson(
            flow,
            nodeTypes,
            handleDeleteNode,
            onEdgeSwitchHandler
          );
          const baseline = JSON.stringify({
            nodes: nextNodes.map((node) => ({
              ...node,
              data: { ...node.data, icon: undefined },
            })),
            edges: nextEdges.map((edge) => ({
              ...edge,
              data: { ...(edge.data || {}), onEdgeSwitch: undefined },
            })),
          });
          setLastLoadedFlowJson(baseline);
          setNodes(nextNodes);
          setEdges(nextEdges);
          if (typeof data.name === 'string' && data.name.trim()) setName(data.name);
          if (typeof data.description === 'string') setDescription(data.description);
          clearSelection();
          setSelectedNode(null);
          setSelectedEdge(null);
          setDrawerOpen(false);
          message.success(t('workflowDesigner.importFlowJsonSuccess'));
        } catch (err) {
          console.error(err);
          message.error(
            `${t('workflowDesigner.importFlowJsonFailed')}${err?.message ? `: ${err.message}` : ''}`
          );
        }
        e.target.value = '';
      };
      reader.onerror = () => {
        message.error(t('workflowDesigner.importFlowJsonFailed'));
        e.target.value = '';
      };
      reader.readAsText(file, 'UTF-8');
    },
    [
      t,
      nodeTypes,
      handleDeleteNode,
      onEdgeSwitchHandler,
      setLastLoadedFlowJson,
      setNodes,
      setEdges,
      setName,
      setDescription,
      clearSelection,
      setSelectedNode,
      setSelectedEdge,
      setDrawerOpen,
    ]
  );

  // 新流程：初次載入完成後，以目前 flowJson 為「已保存」基準，避免未改動也視為 dirty
  const hasSetInitialForNewWorkflow = useRef(false);
  useEffect(() => {
    if (!workflowId && !isWorkflowLoading && !hasSetInitialForNewWorkflow.current) {
      hasSetInitialForNewWorkflow.current = true;
      setLastLoadedFlowJson(flowJson);
    }
  }, [workflowId, isWorkflowLoading, flowJson, setLastLoadedFlowJson]);

  // 關閉分頁/瀏覽器時：若有未保存變更則顯示瀏覽器預設提示
  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // 返回確認彈窗顯示狀態（有未保存變更時點返回會打開）
  const [leaveConfirmVisible, setLeaveConfirmVisible] = useState(false);
  const leaveConfirmNavigateRef = useRef(null);

  // 點擊返回時：若有未保存變更則彈出確認（保存 / 不保存並離開 / 取消）
  const handleBackClick = useCallback(
    (navigate) => {
      if (!isDirty) {
        navigate('/workflow-list');
        return;
      }
      leaveConfirmNavigateRef.current = navigate;
      setLeaveConfirmVisible(true);
    },
    [isDirty]
  );

  const handleLeaveConfirmSaveAndLeave = useCallback(async () => {
    const success = await handleSave();
    if (success) {
      setLeaveConfirmVisible(false);
      if (leaveConfirmNavigateRef.current) {
        leaveConfirmNavigateRef.current('/workflow-list');
        leaveConfirmNavigateRef.current = null;
      }
    }
  }, [handleSave]);

  const handleLeaveConfirmDiscard = useCallback(() => {
    setLeaveConfirmVisible(false);
    if (leaveConfirmNavigateRef.current) {
      leaveConfirmNavigateRef.current('/workflow-list');
      leaveConfirmNavigateRef.current = null;
    }
  }, []);


  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [activeLeftTab, setActiveLeftTab] = useState('design');

  // 條件相關狀態
  const [conditionModalVisible, setConditionModalVisible] = React.useState(false);
  const [conditionGroupModalVisible, setConditionGroupModalVisible] = React.useState(false);
  const [defaultPathModalVisible, setDefaultPathModalVisible] = React.useState(false);
  const [editingCondition, setEditingCondition] = React.useState(null);
  const [editingConditionGroup, setEditingConditionGroup] = React.useState(null);
  const [conditionForm] = Form.useForm();
  const [conditionGroupForm] = Form.useForm();

  // 當編輯條件時預填表單
  React.useEffect(() => {
    if (editingCondition && conditionModalVisible) {
      if (editingCondition.condIndex !== -1) {
        // 編輯現有條件，預填表單
        conditionForm.setFieldsValue({
          variableName: editingCondition.variableName,
          operator: editingCondition.operator,
          value: editingCondition.value,
          jsonKey: editingCondition.jsonKey ?? undefined,
          label: editingCondition.label
        });
      } else {
        // 新增條件，清空表單
        conditionForm.resetFields();
      }
    }
  }, [editingCondition, conditionModalVisible, conditionForm]);

  // 當編輯條件群組時預填表單
  React.useEffect(() => {
    if (editingConditionGroup && conditionGroupModalVisible) {
      if (editingConditionGroup.groupIndex !== -1) {
        // 編輯現有條件群組，預填表單
        conditionGroupForm.setFieldsValue({
          relation: editingConditionGroup.relation,
          outputPath: editingConditionGroup.outputPath
        });
      } else {
        // 新增條件群組，清空表單
        conditionGroupForm.resetFields();
      }
    }
  }, [editingConditionGroup, conditionGroupModalVisible, conditionGroupForm]);

  // 初始化數據
  useEffect(() => {
    if (isReady) {
      initializeData(nodeTypes, setNodes, setEdges, handleDeleteNode, onEdgeSwitchHandler);
    }
  }, [isReady, initializeData]);

  // 確保所有節點都有正確的 onDelete 函數
  useEffect(() => {
    setNodes(nds => nds.map(n => {
      if (n.data.type === 'start') {
        return { ...n, data: { ...n.data, onDelete: null }, draggable: true };
      } else if (!n.data.onDelete) {
        return { ...n, data: { ...n.data, onDelete: handleDeleteNode } };
      }
      return n;
    }));
  }, [setNodes, handleDeleteNode]);

  // 設置條件群組表單初始值
  useEffect(() => {
    if (editingConditionGroup && conditionGroupForm) {
      conditionGroupForm.setFieldsValue({
        relation: editingConditionGroup.relation || 'and',
        outputPath: editingConditionGroup.outputPath || ''
      });
    }
  }, [editingConditionGroup, conditionGroupForm]);

  // 設置條件編輯表單初始值
  useEffect(() => {
    if (editingCondition && conditionForm) {
      conditionForm.setFieldsValue({
        variableName: editingCondition.variableName || '',
        operator: editingCondition.operator || '',
        value: editingCondition.value || '',
        jsonKey: editingCondition.jsonKey ?? undefined,
        label: editingCondition.label || ''
      });
    }
  }, [editingCondition, conditionForm]);

  // 處理拖放
  const handleDrop = (event) => {
    onDrop(event, reactFlowInstanceRef);
  };

  // 處理邊的懸停
  const handleEdgeMouseEnter = (event, edge) => {
    onEdgeMouseEnterHandler(event, edge);
  };

  const handleEdgeMouseLeave = () => {
    onEdgeMouseLeaveHandler();
  };

  // 處理視圖變化
  const handleMove = () => {
    onMoveHandler(selectedEdge);
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <style>{purpleButtonStyle}</style>
      
      {/* 頂部工具欄 */}
      <Toolbar
        onSave={handleSave}
        onExportFlowJson={handleExportFlowJson}
        onImportFlowJsonClick={handleImportFlowJsonClick}
        onOpenProcessVariables={() => setProcessVariablesModalVisible(true)}
        onCopyNodes={copyNodes}
        onPasteNodes={pasteNodes}
        onAlignNodes={alignNodes}
        selectedNodes={selectedNodes}
        onBackClick={handleBackClick}
        t={t}
      />

      <input
        ref={importFlowFileInputRef}
        type="file"
        accept=".json,application/json"
        style={{ display: 'none' }}
        onChange={handleImportFlowJsonFileChange}
      />

      {/* 主要內容區域：左側 Tab 切換、中間單一流程圖、右側屬性抽屜（避免雙畫布導致 AI tab 連線箭頭不顯示） */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0, paddingLeft: 16 }}>
        {/* 左側：Tab 切換 Sidebar / AI 助理 */}
        <div style={{ flexShrink: 0, width: 280, minWidth: 280, display: 'flex', flexDirection: 'column', borderRight: '1px solid #e8e8e8', overflow: 'hidden', backgroundColor: '#fafafa' }}>
          <Tabs
            activeKey={activeLeftTab}
            onChange={setActiveLeftTab}
            className="workflow-designer-main-tabs"
            style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            items={[
              {
                key: 'design',
                label: t('workflowDesigner.tabDesign') || '流程設計',
                children: (
                  <div style={{ flex: 1, overflowY: 'auto', padding: 16, height: '100%' }}>
                    <Sidebar
                      isToolbarCollapsed={isToolbarCollapsed}
                      name={name}
                      setName={setName}
                      description={description}
                      setDescription={setDescription}
                      nodeTypeDefinitions={nodeTypeDefinitions}
                      loadingNodeTypes={loadingNodeTypes}
                      nodeTypes={nodeTypes}
                      nodes={nodes}
                      onDragStart={onDragStart}
                      t={t}
                    />
                  </div>
                )
              },
              {
                key: 'ai',
                label: t('workflowDesigner.tabAIAssistant') || 'AI 流程助理',
                children: (
                  <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <WorkflowDesignerAIChat
                      workflowDefinitionId={workflowId}
                      flowJson={flowJson}
                      onSuccess={() => {}}
                      isGenerating={isAiGenerating}
                      setIsGenerating={setIsAiGenerating}
                      nodeTypes={nodeTypes}
                      handleDeleteNode={handleDeleteNode}
                      onEdgeSwitch={onEdgeSwitchHandler}
                      setNodes={setNodes}
                      setEdges={setEdges}
                      t={t}
                    />
                  </div>
                )
              }
            ]}
          />
        </div>

        {/* 中間：單一 WorkflowCanvas，兩個 tab 共用，確保連線箭頭一致顯示 */}
        <div style={{ flex: 1, position: 'relative', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <AIGeneratingOverlay visible={isAiGenerating} t={t} />
          <WorkflowCanvas
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onDrop={handleDrop}
          onDragOver={onDragOver}
          onNodeClick={onNodeClick}
          onNodeDoubleClick={onNodeDoubleClick}
          onEdgeClick={onEdgeClick}
          onEdgeMouseEnter={handleEdgeMouseEnter}
          onEdgeMouseLeave={handleEdgeMouseLeave}
          nodeTypes={nodeTypes}
          nodeTypesComponents={nodeTypesComponents}
          edgeTypes={edgeTypes}
          onEdgeSwitch={onEdgeSwitchHandler}
          onMove={handleMove}
          handleInit={handleInit}
          selectedEdge={selectedEdge}
          hoveredEdge={hoveredEdge}
          onEdgeDelete={onEdgeDelete}
          reactFlowInstanceRef={reactFlowInstanceRef}
          onNodeSelect={handleNodeSelect}
          onCanvasClick={handleCanvasClick}
          onKeyDown={handleKeyDown}
          onSelectionChange={handleSelectionChange}
          t={t}
          isWorkflowLoading={isWorkflowLoading}
        />
        </div>

        {/* 右側屬性編輯 */}
        <NodePropertyDrawer
          selectedNode={selectedNode}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          form={form}
          handleNodeDataChange={handleNodeDataChange}
          processVariables={processVariables}
          nodes={nodes}
          edges={edges}
          workflowId={workflowId}
          workflowName={name}
          t={t}
          templates={templates}
          metaTemplates={metaTemplates}
          eforms={eforms}
          isTemplateModalVisible={isTemplateModalVisible}
          setIsTemplateModalVisible={setIsTemplateModalVisible}
          isUserModalVisible={isUserModalVisible}
          setIsUserModalVisible={setIsUserModalVisible}
          isEFormModalVisible={isEFormModalVisible}
          setIsEFormModalVisible={setIsEFormModalVisible}
          isRecipientModalVisible={isRecipientModalVisible}
          setIsRecipientModalVisible={setIsRecipientModalVisible}
          conditionModalVisible={conditionModalVisible}
          setConditionModalVisible={setConditionModalVisible}
          conditionGroupModalVisible={conditionGroupModalVisible}
          setConditionGroupModalVisible={setConditionGroupModalVisible}
          defaultPathModalVisible={defaultPathModalVisible}
          setDefaultPathModalVisible={setDefaultPathModalVisible}
          editingCondition={editingCondition}
          setEditingCondition={setEditingCondition}
          editingConditionGroup={editingConditionGroup}
          setEditingConditionGroup={setEditingConditionGroup}
          conditionForm={conditionForm}
          conditionGroupForm={conditionGroupForm}
          handleSelectTemplate={handleSelectTemplate}
          handleSelectUser={handleSelectUser}
          handleSelectEForm={handleSelectEForm}
          onSaveConditionGroup={(values) => {
            if (editingConditionGroup && selectedNode) {
              const conditionGroup = {
                id: editingConditionGroup.id || `group_${Date.now()}`,
                relation: values.relation,
                outputPath: values.outputPath,
                conditions: editingConditionGroup.conditions || []
              };
              
              const currentGroups = selectedNode.data.conditionGroups || [];
              const newGroups = [...currentGroups];
              
              if (editingConditionGroup.groupIndex === -1) {
                // 新增條件群組
                newGroups.push(conditionGroup);
              } else {
                // 更新現有條件群組
                newGroups[editingConditionGroup.groupIndex] = conditionGroup;
              }
              
              handleNodeDataChange({ conditionGroups: newGroups });
            }
            setConditionGroupModalVisible(false);
            setEditingConditionGroup(null);
            conditionGroupForm.resetFields();
          }}
          onEditCondition={(condition, condIndex) => {
            setEditingCondition({ ...condition, groupIndex: editingConditionGroup.groupIndex, condIndex });
            setConditionModalVisible(true);
          }}
          onAddCondition={() => {
            setEditingCondition({ groupIndex: editingConditionGroup.groupIndex, condIndex: -1 });
            setConditionModalVisible(true);
          }}
          onDeleteCondition={(condIndex) => {
            const currentConditions = editingConditionGroup.conditions || [];
            const newConditions = currentConditions.filter((_, i) => i !== condIndex);
            setEditingConditionGroup({ ...editingConditionGroup, conditions: newConditions });
          }}
          onSelectPath={() => {}}
          // 新增：Time Validator 模板選擇相關
          templateModalSource={templateModalSource}
          setTemplateModalSource={setTemplateModalSource}
          handleTimeValidatorTemplateSelect={handleTimeValidatorTemplateSelect}
        />
      </div>

      {/* 模態框組件 */}
      <TemplateModal
        visible={isTemplateModalVisible}
        onCancel={() => setIsTemplateModalVisible(false)}
        templates={templates}
        metaTemplates={metaTemplates}
        onSelectTemplate={(template, isMetaTemplate) => {
          // 檢查是否有 QR Code 模板選擇請求
          const qrCodeMessageType = window.qrCodeTemplateSelectMessageType;
          if (qrCodeMessageType) {
            console.log('🎯 主要 TemplateModal 處理 QR Code 模板選擇:', { template: template.name, isMetaTemplate, messageType: qrCodeMessageType });
            // 發送 QR Code 模板選擇事件
            window.dispatchEvent(new CustomEvent('qrCodeTemplateSelected', {
              detail: {
                template,
                isMetaTemplate,
                messageType: qrCodeMessageType
              }
            }));
            window.qrCodeTemplateSelectMessageType = null; // 清除
            setIsTemplateModalVisible(false);
          } else if (window.waitReplyTemplateSelectMessageType) {
            // 檢查是否有 Wait Reply 模板選擇請求
            const waitReplyMessageType = window.waitReplyTemplateSelectMessageType;
            console.log('🎯 主要 TemplateModal 處理 Wait Reply 模板選擇:', { template: template.name, isMetaTemplate, messageType: waitReplyMessageType });
            // 發送 Wait Reply 模板選擇事件
            window.dispatchEvent(new CustomEvent('waitReplyTemplateSelected', {
              detail: {
                template,
                isMetaTemplate,
                messageType: waitReplyMessageType
              }
            }));
            window.waitReplyTemplateSelectMessageType = null; // 清除
            setIsTemplateModalVisible(false);
          } else if (templateModalSource) {
            console.log('🎯 主要 TemplateModal 處理 Time Validator 模板選擇:', { template: template.name, isMetaTemplate, source: templateModalSource });
            // 調用 NodePropertyDrawer 的處理函數
            handleTimeValidatorTemplateSelect(template, isMetaTemplate);
            setIsTemplateModalVisible(false);
          } else {
            // 一般的模板選擇
            handleSelectTemplate(template, isMetaTemplate);
            setIsTemplateModalVisible(false);
          }
        }}
        t={t}
      />

      <UserModal
        visible={isUserModalVisible}
        onCancel={() => setIsUserModalVisible(false)}
        users={users}
        onSelectUser={(user) => {
          handleSelectUser(user);
          setIsUserModalVisible(false);
        }}
        t={t}
      />

      <EFormModal
        visible={isEFormModalVisible}
        onCancel={() => setIsEFormModalVisible(false)}
        eforms={eforms}
        onSelectEForm={(eform) => {
          handleSelectEForm(eform);
          setIsEFormModalVisible(false);
        }}
        t={t}
      />

      <ProcessVariablesModal
        visible={processVariablesModalVisible}
        onCancel={() => setProcessVariablesModalVisible(false)}
        processVariables={processVariables}
        selectedProcessVariable={selectedProcessVariable}
        editingProcessVariable={editingProcessVariable}
        processVariableForm={processVariableForm}
        onAddProcessVariable={() => {
          // 新變量預設 displayOrder = 現有最大 + 1，避免初始全是 0 導致排序不可控
          const maxOrder = Array.isArray(processVariables) && processVariables.length > 0
            ? Math.max(...processVariables.map(v => v.displayOrder ?? 0))
            : -1;
          const newVariable = {
            id: null,
            workflowDefinitionId: workflowId,
            variableName: '',
            displayName: '',
            dataType: 'string',
            description: '',
            isRequired: false,
            defaultValue: '',
            validationRules: '',
            jsonSchema: '',
            showInList: false,
            showInInstanceList: false,
            displayLabel: '',
            displayOrder: maxOrder + 1,
            jsonKeyDisplaySettings: null,
            jsonKeyDisplaySettingsList: []
          };
          setEditingProcessVariable(newVariable);
          setSelectedProcessVariable(newVariable);
          processVariableForm.setFieldsValue(newVariable);
        }}
        onEditProcessVariable={(variable) => {
          // 把後端 jsonKeyDisplaySettings (JSON 字串) 解析為陣列供 Form 使用
          let jsonKeyList = [];
          if (variable.jsonKeyDisplaySettings) {
            try {
              const parsed = typeof variable.jsonKeyDisplaySettings === 'string'
                ? JSON.parse(variable.jsonKeyDisplaySettings)
                : variable.jsonKeyDisplaySettings;
              if (Array.isArray(parsed)) jsonKeyList = parsed;
            } catch (e) {
              console.warn('解析 jsonKeyDisplaySettings 失敗:', e?.message);
            }
          }
          const formValues = {
            ...variable,
            showInList: !!variable.showInList,
            showInInstanceList: !!variable.showInInstanceList,
            displayOrder: variable.displayOrder ?? 0,
            jsonKeyDisplaySettingsList: jsonKeyList
          };
          setEditingProcessVariable(variable);
          setSelectedProcessVariable(variable);
          processVariableForm.setFieldsValue(formValues);
        }}
        onDeleteProcessVariable={async (variableId) => {
          try {
            console.log('刪除流程變量:', variableId);
            
            // 調用 API 刪除
            const { apiService } = await import('./services/apiService');
            const success = await apiService.deleteProcessVariable(variableId);
            
            if (success) {
              // 重新載入流程變量列表
              const updatedVariables = await apiService.fetchProcessVariables(workflowId);
              setProcessVariables(updatedVariables);
              
              // 重置編輯狀態
              setEditingProcessVariable(null);
              setSelectedProcessVariable(null);
              processVariableForm.resetFields();
              
              console.log('流程變量刪除成功');
            } else {
              console.error('流程變量刪除失敗');
            }
          } catch (error) {
            console.error('刪除流程變量失敗:', error);
          }
        }}
        onSaveProcessVariable={async (values) => {
          try {
            console.log('保存流程變量:', values);

            // 將前端的 jsonKeyDisplaySettingsList（陣列）序列化為後端期望的 JSON 字串
            const { jsonKeyDisplaySettingsList, ...rest } = values || {};
            const jsonKeyDisplaySettings = Array.isArray(jsonKeyDisplaySettingsList) && jsonKeyDisplaySettingsList.length > 0
              ? JSON.stringify(jsonKeyDisplaySettingsList)
              : null;

            const variableData = {
              ...rest,
              workflowDefinitionId: workflowId,
              id: editingProcessVariable?.id || null,
              showInList: !!rest.showInList,
              showInInstanceList: !!rest.showInInstanceList,
              displayLabel: rest.displayLabel || null,
              displayOrder: typeof rest.displayOrder === 'number' ? rest.displayOrder : 0,
              jsonKeyDisplaySettings
            };
            
            // 檢查是更新還是創建
            const isUpdate = editingProcessVariable && editingProcessVariable.id;
            
            // 調用 API 保存
            const { apiService } = await import('./services/apiService');
            const success = await apiService.saveProcessVariable(variableData, isUpdate, editingProcessVariable?.id);
            
            if (success) {
              // 重新載入流程變量列表
              const updatedVariables = await apiService.fetchProcessVariables(workflowId);
              setProcessVariables(updatedVariables);
              
              // 重置編輯狀態
              setEditingProcessVariable(null);
              setSelectedProcessVariable(null);
              processVariableForm.resetFields();
              
              console.log('流程變量保存成功');
            } else {
              console.error('流程變量保存失敗');
            }
          } catch (error) {
            console.error('保存流程變量失敗:', error);
          }
        }}
        onReorderProcessVariable={async (variableId, direction) => {
          // 列表依 displayOrder 升冪排序；交換相鄰兩個 PV 的 displayOrder 後同時 PUT 兩筆。
          try {
            if (!variableId || !Array.isArray(processVariables) || processVariables.length < 2) return;

            const sorted = processVariables
              .slice()
              .sort((a, b) => {
                const ao = a.displayOrder ?? 0;
                const bo = b.displayOrder ?? 0;
                if (ao !== bo) return ao - bo;
                return String(a.variableName || '').localeCompare(String(b.variableName || ''));
              });

            const idx = sorted.findIndex(v => v.id === variableId);
            if (idx < 0) return;
            const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
            if (swapIdx < 0 || swapIdx >= sorted.length) return;

            const current = sorted[idx];
            const neighbor = sorted[swapIdx];
            const currentOrder = current.displayOrder ?? 0;
            const neighborOrder = neighbor.displayOrder ?? 0;
            // 若兩者的 displayOrder 相同，直接拉開 1 個間距
            const targetOrders = currentOrder === neighborOrder
              ? (direction === 'up' ? [neighborOrder, neighborOrder + 1] : [neighborOrder, neighborOrder - 1])
              : [neighborOrder, currentOrder];

            const buildPayload = (variable, newOrder) => ({
              ...variable,
              showInList: !!variable.showInList,
              showInInstanceList: !!variable.showInInstanceList,
              displayLabel: variable.displayLabel || null,
              displayOrder: newOrder,
              // 後端期望 jsonKeyDisplaySettings 為 JSON 字串
              jsonKeyDisplaySettings: variable.jsonKeyDisplaySettings
                ? (typeof variable.jsonKeyDisplaySettings === 'string'
                    ? variable.jsonKeyDisplaySettings
                    : JSON.stringify(variable.jsonKeyDisplaySettings))
                : null
            });

            const { apiService } = await import('./services/apiService');
            await Promise.all([
              apiService.saveProcessVariable(buildPayload(current, targetOrders[0]), true, current.id),
              apiService.saveProcessVariable(buildPayload(neighbor, targetOrders[1]), true, neighbor.id)
            ]);
            const updatedVariables = await apiService.fetchProcessVariables(workflowId);
            setProcessVariables(updatedVariables);
          } catch (error) {
            console.error('調整流程變量順序失敗:', error);
          }
        }}
        onCancelProcessVariableEdit={() => {
          setEditingProcessVariable(null);
          processVariableForm.resetFields();
        }}
        t={t}
      />

      <ConditionGroupModal
        visible={conditionGroupModalVisible}
        onCancel={() => {
          setConditionGroupModalVisible(false);
          setEditingConditionGroup(null);
          conditionGroupForm.resetFields();
        }}
        editingConditionGroup={editingConditionGroup}
        conditionGroupForm={conditionGroupForm}
        selectedNode={selectedNode}
        getAvailableOutputPaths={(nodeId) => getAvailableOutputPaths(nodeId, edges, nodes, t)}
        onSave={(values) => {
          if (editingConditionGroup && selectedNode) {
            const conditionGroup = {
              id: editingConditionGroup.id || `group_${Date.now()}`,
              relation: values.relation,
              outputPath: values.outputPath,
              conditions: editingConditionGroup.conditions || []
            };
            
            const currentGroups = selectedNode.data.conditionGroups || [];
            const newGroups = [...currentGroups];
            
            if (editingConditionGroup.groupIndex === -1) {
              // 新增條件群組
              newGroups.push(conditionGroup);
            } else {
              // 更新現有條件群組
              newGroups[editingConditionGroup.groupIndex] = conditionGroup;
            }
            
            handleNodeDataChange({ conditionGroups: newGroups });
          }
          setConditionGroupModalVisible(false);
          setEditingConditionGroup(null);
          conditionGroupForm.resetFields();
        }}
        onEditCondition={(condition, condIndex) => {
          setEditingCondition({ ...condition, groupIndex: editingConditionGroup.groupIndex, condIndex });
          setConditionModalVisible(true);
        }}
        onAddCondition={() => {
          console.log('=== 開始添加條件 ===');
          console.log('editingConditionGroup:', editingConditionGroup);
          console.log('groupIndex:', editingConditionGroup.groupIndex);
          const newEditingCondition = { groupIndex: editingConditionGroup.groupIndex, condIndex: -1 };
          console.log('newEditingCondition:', newEditingCondition);
          setEditingCondition(newEditingCondition);
          setConditionModalVisible(true);
          console.log('條件模態框已打開');
        }}
        onDeleteCondition={(condIndex) => {
          const currentConditions = editingConditionGroup.conditions || [];
          const newConditions = currentConditions.filter((_, i) => i !== condIndex);
          
          // 更新編輯中的條件群組狀態
          setEditingConditionGroup({ ...editingConditionGroup, conditions: newConditions });
          
          // 同時更新節點數據
          if (selectedNode && editingConditionGroup.groupIndex >= 0) {
            const currentGroups = selectedNode.data.conditionGroups || [];
            const newGroups = [...currentGroups];
            if (newGroups[editingConditionGroup.groupIndex]) {
              newGroups[editingConditionGroup.groupIndex].conditions = newConditions;
              handleNodeDataChange({ conditionGroups: newGroups });
            }
          }
        }}
        t={t}
      />

      <ConditionModal
        visible={conditionModalVisible}
        onCancel={() => {
          setConditionModalVisible(false);
          setEditingCondition(null);
          conditionForm.resetFields();
        }}
        editingCondition={editingCondition}
        conditionForm={conditionForm}
        processVariables={processVariables}
        onSave={(values) => {
          console.log('=== 保存條件 ===');
          console.log('values:', values);
          console.log('editingCondition:', editingCondition);
          console.log('selectedNode:', selectedNode);
          console.log('editingConditionGroup:', editingConditionGroup);
          
          if (editingCondition && selectedNode) {
            const condition = {
              id: editingCondition.id || `condition_${Date.now()}`,
              variableName: values.variableName,
              operator: values.operator,
              value: values.value,
              jsonKey: values.jsonKey || null,
              label: values.label || `${values.variableName} ${values.operator} ${values.value}`
            };
            console.log('新條件:', condition);
            
            const currentGroups = selectedNode.data.conditionGroups || [];
            console.log('當前條件群組:', currentGroups);
            const newGroups = [...currentGroups];
            console.log('新條件群組數組:', newGroups);
            
            if (editingCondition.groupIndex === -1) {
              // 新增條件群組的情況，直接更新 editingConditionGroup
              console.log('新增條件群組，直接更新 editingConditionGroup');
              const currentConditions = editingConditionGroup?.conditions || [];
              const newConditions = [...currentConditions, condition];
              console.log('新的條件列表:', newConditions);
              
              const updatedGroup = {
                ...editingConditionGroup,
                conditions: newConditions
              };
              console.log('更新後的 editingConditionGroup:', updatedGroup);
              setEditingConditionGroup(updatedGroup);
              console.log('editingConditionGroup 狀態已更新');
            } else if (editingCondition.groupIndex >= 0 && newGroups[editingCondition.groupIndex]) {
              // 編輯現有條件群組的情況
              console.log('目標群組索引:', editingCondition.groupIndex);
              console.log('目標群組:', newGroups[editingCondition.groupIndex]);
              
              if (!newGroups[editingCondition.groupIndex].conditions) {
                newGroups[editingCondition.groupIndex].conditions = [];
                console.log('初始化條件數組');
              }
              
              if (editingCondition.condIndex === -1) {
                // 新增條件
                console.log('添加新條件到群組');
                newGroups[editingCondition.groupIndex].conditions.push(condition);
                console.log('添加後的條件列表:', newGroups[editingCondition.groupIndex].conditions);
              } else {
                // 更新現有條件
                console.log('更新現有條件');
                newGroups[editingCondition.groupIndex].conditions[editingCondition.condIndex] = condition;
              }
              
              console.log('更新節點數據前的完整群組:', newGroups);
              handleNodeDataChange({ conditionGroups: newGroups });
              console.log('節點數據已更新');
              
              // 更新編輯中的條件群組狀態 - 確保狀態同步
              const updatedGroup = { 
                ...editingConditionGroup, 
                conditions: [...newGroups[editingCondition.groupIndex].conditions] // 創建新數組確保引用更新
              };
              console.log('更新後的 editingConditionGroup:', updatedGroup);
              setEditingConditionGroup(updatedGroup);
              console.log('editingConditionGroup 狀態已更新');
            } else {
              console.log('條件保存失敗：無效的群組索引或群組不存在');
            }
          } else {
            console.log('條件保存失敗：缺少必要參數');
          }
          setConditionModalVisible(false);
          setEditingCondition(null);
          conditionForm.resetFields();
          console.log('條件模態框已關閉');
        }}
        t={t}
      />

      <DefaultPathModal
        visible={defaultPathModalVisible}
        onCancel={() => setDefaultPathModalVisible(false)}
        selectedNode={selectedNode}
        availablePaths={selectedNode ? getAvailableOutputPaths(selectedNode.id, edges, nodes, t) : []}
        onSelectPath={(pathId) => {
          if (selectedNode) {
            handleNodeDataChange({ defaultPath: pathId });
          }
          setDefaultPathModalVisible(false);
        }}
        t={t}
      />

      {/* 離開前未保存變更確認 */}
      <Modal
        title={t('workflowDesigner.unsavedChangesTitle')}
        open={leaveConfirmVisible}
        onCancel={() => { setLeaveConfirmVisible(false); leaveConfirmNavigateRef.current = null; }}
        footer={[
          <Button key="cancel" onClick={() => { setLeaveConfirmVisible(false); leaveConfirmNavigateRef.current = null; }}>
            {t('workflowDesigner.cancel')}
          </Button>,
          <Button key="discard" onClick={handleLeaveConfirmDiscard}>
            {t('workflowDesigner.leaveWithoutSaving')}
          </Button>,
          <Button key="save" type="primary" onClick={handleLeaveConfirmSaveAndLeave}>
            {t('workflowDesigner.saveAndLeave')}
          </Button>,
        ]}
      >
        {t('workflowDesigner.unsavedChangesContent')}
      </Modal>
    </div>
  );
};

export default WhatsAppWorkflowDesignerRefactored;
